import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogPage } from '../types/catalog-enrichment.types';
import type { CatalogEnvelope, CatalogListEnvelope } from '../types/catalog-service.api.types';
import type { RunRead } from '../types/run.api.types';
import type {
  ArchiveResult,
  BulkArchive,
  RunCreator,
  RunFailureGroups,
  RunsKpi,
  RunStatusCounts,
} from '../types/runs.api.types';
import type {
  RunFailuresParams,
  RunsAggregateParams,
  RunsFilterParams,
  RunsListParams,
} from '../types/runs.types';
import { toPage, toRepeatedSearchParams, toSearchParams, unwrap } from '../utils/envelope';

/** One page of this tenant's runs under the list filters, tab and sort (newest first by
 *  default); `createdBy` / `currentStage` go out as repeated keys. */
export async function listRuns(params: RunsListParams): Promise<CatalogPage<RunRead>> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.list(), {
      searchParams: toRepeatedSearchParams({ ...params }),
    })
    .json<CatalogListEnvelope<RunRead>>();
  return toPage(body, (row) => row);
}

/** Count per run-list tab under the same filters (the server ignores `status` here). */
export async function getRunStatusCounts(params: RunsFilterParams): Promise<RunStatusCounts> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.statusCounts(), {
      searchParams: toRepeatedSearchParams({ ...params }),
    })
    .json<CatalogEnvelope<RunStatusCounts>>();
  return unwrap(body);
}

/** Who started runs under the list filters — the "Created by" filter's options, most runs first
 *  (≤ 100; only actors with an id, so schedule-started runs are never an option). */
export async function getRunCreators(params: RunsFilterParams): Promise<RunCreator[]> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.creators(), {
      searchParams: toRepeatedSearchParams({ ...params }),
    })
    .json<CatalogEnvelope<RunCreator[]>>();
  return unwrap(body);
}

/** The 7-day KPI strip (batches, delivered, latency, needs attention) with week-on-week deltas. */
export async function getRunsSummary(params: RunsAggregateParams): Promise<RunsKpi> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.summary(), { searchParams: toSearchParams({ ...params }) })
    .json<CatalogEnvelope<RunsKpi>>();
  return unwrap(body);
}

/** Failed and soft-failed runs grouped by cause; the server defaults to the last 7 days unless
 *  `since`/`until` narrow it (the failure-patterns view's own time-window selector, S12). */
export async function getRunFailures(params: RunFailuresParams): Promise<RunFailureGroups> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.failures(), {
      searchParams: toSearchParams({ ...params }),
    })
    .json<CatalogEnvelope<RunFailureGroups>>();
  return unwrap(body);
}

/**
 * Hide one run from the default list (the row action menu). A run that may still
 * execute or deliver is a 409 (`details.code` RUN_IN_FLIGHT / RUN_DELIVERING), not
 * a 200 with a refused outcome — unlike the bulk endpoint below.
 */
export async function archiveRun(runId: string): Promise<ArchiveResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.runs.archive(runId))
    .json<CatalogEnvelope<ArchiveResult>>();
  return unwrap(body);
}

/** Restore one archived run to the default list (the row action menu). */
export async function unarchiveRun(runId: string): Promise<ArchiveResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.runs.unarchive(runId))
    .json<CatalogEnvelope<ArchiveResult>>();
  return unwrap(body);
}

/** Hide many runs from the default list; each run gets its own outcome (never fails whole). */
export async function archiveRuns(payload: BulkArchive): Promise<ArchiveResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.runs.bulkArchive(), { json: payload })
    .json<CatalogEnvelope<ArchiveResult>>();
  return unwrap(body);
}

/** Restore many archived runs to the default list; per-run outcomes as for archive. */
export async function unarchiveRuns(payload: BulkArchive): Promise<ArchiveResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.runs.bulkUnarchive(), { json: payload })
    .json<CatalogEnvelope<ArchiveResult>>();
  return unwrap(body);
}
